/* eslint-disable max-lines */
import React, { FC, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import scrollToElement from 'scroll-to-element';

import { LegislationContext } from '../LegislationContext';
import { useBreadcrumbFetcher } from '../../../hooks/useBreadcrumbFetcher';
import { useLinkedLegislationsFetcher } from './hooks/useLinkedLegislationsFetcher';

import { ContentHeader } from '../../../../components/content-header/ContentHeader';
import { Container, Row } from '../../../../components/grid/Grid';
import { Versions } from './sections/versions/Versions';
import { Text } from './sections/text/Text';
import { Commentaries } from './sections/commentaries/Commentaries';
import { Decisions } from './sections/decisions/Decisions';
import { PreparatoryWork } from './sections/preparatory-work/PreparatoryWork';
import { ReportTimeSystemEntity } from '../../../../report-time-system/ReportTimeSystemEntity';
import { ContentHeaderTopComponent } from './components/content-header-top-component/ContentHeaderTopComponent';
import { Navigation } from './components/navigation/Navigation';

import { ABROGE } from '../../../../../../shared/constants/legislationArticleApplicability';

import { isValidHash } from '../../../utils/legislationUtils';
import { getFromUrlOrBuildDateVersionKey } from './utils/utils';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import {
  getParametersFromUrl,
  rebuildUrlWithoutSource,
} from '../../../../utils/url-utils/urlUtils';

import styles from './legislation_page.module.scss';

export const LegislationPage: FC = () => {
  const history = useHistory();
  const { search, hash } = useLocation();
  const {
    initialVersionId,
    allVersionsIds,
    legislationInfos,
    legislationInfosAll,
    articleId,
    setArticleId,
    commentaries,
    decisions,
    preparatoryWork,
    referencesDecisionsIds,
    QPCDecisionsIds,
    quotedByLegislations,
    textNature,
    fullTitle,
    textId,
    legislationInfosPreviousAndNext: {
      legislationInfosPrevious,
      legislationInfosNext,
    },
  } = useContext(LegislationContext);
  const [fakeLoader, setFakeLoader] = useState(false);

  const dateKey = getFromUrlOrBuildDateVersionKey(
    search,
    legislationInfos?.endingDate ?? '',
  );

  const breadcrumb = useBreadcrumbFetcher(
    textId,
    articleId,
    dateKey,
    'article',
  );
  const linkedLegislation = useLinkedLegislationsFetcher(
    articleId,
    'created_by,codified_by,modified_by,abrogated_by,transferred_by,outdated_by,separated_by,cancelled_by,new_texts,old_texts',
  );

  useEffect(() => {
    if (hash && isValidHash(hash)) {
      scrollToElement(hash, {
        offset: -120,
        duration: 500,
      });
    }
  });

  useEffect(() => {
    rebuildUrlWithoutSource();
  }, []);

  useEffect(() => {
    if (initialVersionId) {
      const data = getParametersFromUrl(search);
      doctrineAnalytics('Static LegislationLoaded', {
        article_version_id: articleId,
        article_id: initialVersionId,
        legislationType: textNature,
        text_id: textId,
        ...data,
      });
    }
  }, [initialVersionId]);

  if (!legislationInfos) {
    return null;
  }

  const handleVersionChange = (newArticleId: string) => {
    if (articleId === newArticleId) return null;
    setFakeLoader(true);
    doctrineAnalytics('Legislation VersionClicked', {
      legislationType: textNature,
      article_id: initialVersionId,
      article_version_id: newArticleId,
      text_id: textId,
      reportOriginalQueryKey: true,
    });
    const newArticle = legislationInfosAll.find(
      (legislation) => legislation.articleId === newArticleId,
    );
    history.push(
      newArticle?.isLatestVersion
        ? `${initialVersionId}`
        : `${initialVersionId}?version=${newArticleId}`,
    );
    setTimeout(() => setFakeLoader(false), 500);
    setArticleId(newArticleId);
  };

  return (
    <ReportTimeSystemEntity entityType="law" readKey="" pageId={articleId}>
      <div id="top">
        <ContentHeader
          topComponent={
            <ContentHeaderTopComponent
              breadcrumbs={breadcrumb}
              previous={legislationInfosPrevious}
              next={legislationInfosNext}
            />
          }
          title={
            <div className={styles.title}>
              {legislationInfos.applicability === ABROGE ? (
                <FormattedMessage
                  id="page/LegislationPage.tsx/1"
                  values={{ fullTitle }}
                />
              ) : (
                fullTitle
              )}
            </div>
          }
          subComponent={
            <Navigation containerClassName={styles.navigationContainer} />
          }
        />
      </div>
      <Container>
        <div id="text" className={styles.sectionMargins}>
          <Row>
            <Versions
              legislationInfos={legislationInfos}
              legislationInfosAll={legislationInfosAll}
              articleId={articleId}
              handleVersionChange={handleVersionChange}
            />
          </Row>
          <Row>
            <Text
              legislationInfos={legislationInfos}
              linkedLegislation={linkedLegislation}
              articleId={articleId}
              fakeLoader={fakeLoader}
              quotedByLegislations={quotedByLegislations}
              articleTitle={legislationInfos.title}
              textNature={textNature}
              initialVersionId={initialVersionId}
            />
          </Row>
        </div>
        {!!commentaries?.data?.hits?.length && (
          <div id="commentaries" className={styles.sectionMargins}>
            <Row>
              <Commentaries
                commentaries={commentaries}
                articleTitle={legislationInfos.title}
                articleId={articleId}
                articleIds={allVersionsIds}
                textNature={textNature}
                initialVersionId={initialVersionId}
                textId={textId}
              />
            </Row>
          </div>
        )}
        <div id="decisions" className={classNames(styles.sectionMargins)}>
          <Row>
            <Decisions
              referencesDecisionsIds={referencesDecisionsIds}
              QPCDecisionsIds={QPCDecisionsIds}
              decisions={decisions}
              articleIds={allVersionsIds}
              articleTitle={legislationInfos.title}
              articleId={articleId}
              textNature={textNature}
              initialVersionId={initialVersionId}
            />
          </Row>
        </div>
        {!!preparatoryWork?.data?.hits?.length && (
          <div
            id="preparatory-work"
            className={classNames(
              styles.sectionMargins,
              styles.lastSectionMargins,
            )}
          >
            <Row>
              <PreparatoryWork
                preparatoryWork={preparatoryWork}
                articleIds={allVersionsIds}
                articleTitle={legislationInfos.title}
                articleId={articleId}
                textNature={textNature}
                initialVersionId={initialVersionId}
              />
            </Row>
          </div>
        )}
      </Container>
    </ReportTimeSystemEntity>
  );
};
